import './App.css';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import Loader from './components/Loader/Loader';
import Header from './components/Header/Header';
import Navbar from './components/Navbar/Navbar';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import OfflineNotice from './components/OfflineNotice/OfflineNotice';
import { CartProvider } from './store/CartContext';
import { FavoritesProvider } from './store/FavoritesContext';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from './Redux/Reducers/authSlice';

// Code-split each page so a route only downloads the JS it needs.
// This keeps the initial bundle small and lets pages like Checkout
// load without pulling in heavy deps (leaflet, maps, etc.) from other routes.
const HomePages = lazy(() => import('./Pages/HomePages/HomePages'));
const Products = lazy(() => import('./Pages/Products/Products'));
const AboutUs = lazy(() => import('./Pages/AboutUs/AboutUs'));
const ContactUs = lazy(() => import('./Pages/ContactUs/ContactUs'));
const Blog = lazy(() => import('./Pages/Blog/Blog'));
const NotFound = lazy(() => import('./Pages/NotFound/NotFound'));
const Cart = lazy(() => import('./Pages/Cart/Cart'));
const Favorite = lazy(() => import('./Pages/Favorite/Favorite'));
const Categories = lazy(() => import('./Pages/Categories/Categories'));
const Checkout = lazy(() => import('./Pages/Checkout/Checkout'));
const Profile = lazy(() => import('./Pages/Profile/Profile'));
const Login = lazy(() => import('./Pages/Login/Login'));
const Signup = lazy(() => import('./Pages/Signup/Signup'));
const OrderSuccess = lazy(() => import('./Pages/OrderSuccess/OrderSuccess'));
const ProductsDetails = lazy(() => import('./Pages/ProductsDetails/ProductsDetails'));

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const user = useSelector(state => state.auth.user);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const isAuthRoute = location.pathname === '/login' || location.pathname === '/signup';

  return (
    <CartProvider>
      <FavoritesProvider>
        <div className="app">
          {!isAuthRoute && <Header user={user} onLogout={handleLogout} />}
          {!isAuthRoute && <Navbar />}
          <ScrollToTop />
          <OfflineNotice />
          <Suspense fallback={<Loader />}>
            <Routes>
                {/* Public Routes */}
                <Route 
                  path="/login" 
                  element={
                    isAuthenticated ? 
                    <HomePages /> : 
                    <Login />
                  } 
                />
                <Route 
                  path="/signup" 
                  element={
                    isAuthenticated ? 
                    <HomePages /> : 
                    <Signup />
                  } 
                />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <HomePages />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Products />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/favorite"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Favorite />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/about"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <AboutUs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contact"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <ContactUs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/blog"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Blog />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/categories"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Categories />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cart"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Cart />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Checkout />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Profile user={user} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/product/:id"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <ProductsDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/order-success"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <OrderSuccess />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
          </Suspense>
        </div>
      </FavoritesProvider>
    </CartProvider>
  );
};

export default App;